/* React Higher Order Component which fetches organization privacy policies for organization where id = props.orgId
    Child (WrappedComponent) will only render once privacy policies are successfully fetched.
    child will receive props: {
        orgId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        orgPrivacyPolicies: PropTypes.array.isRequired,
        canEditPrivacyPolicies: PropTypes.bool.isRequired,
    }
*/

import React from 'react';
import PropTypes from 'prop-types';
const uuid = require('uuid');

const Globals = require('../../../../app/globals');
const Strings = require('../../../../app/shared/Strings');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const OrganizationActions = require('../../actions/OrganizationActions');
const OrganizationStore = require('../../stores/OrganizationStore');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    forceRefresh: PropTypes.bool,
    channelPermissions: PropTypes.object.isRequired,
    userPermissions: PropTypes.object.isRequired,
};

const defaultProps = {
    forceRefresh: false,
};

export default function withOrganizationPrivacyPolicies(WrappedComponent) {
    class WithOrganizationPrivacyPolicies extends React.Component {
        constructor(props) {
            super(props);

            const orgStoreState = OrganizationStore.getStateOrganization(props.orgId);
            this.state = {
                orgPrivacyPolicies: orgStoreState.orgPrivacyPolicies,
                getActionId: null,
                fatalError: null
            };

            this.getOrgPrivacyPolicies = this.getOrgPrivacyPolicies.bind(this);
            this.isDataLoaded = this.isDataLoaded.bind(this);
            this.unsubscribes = [
                OrganizationStore.listen(this.onOrganizationStoreChange.bind(this))
            ];
        }

        componentDidMount() {
            this.getOrgPrivacyPolicies(this.props.orgId, this.props.forceRefresh);
        }

        componentDidUpdate(prevProps) {
            if (prevProps.orgId !== this.props.orgId) {
                this.getOrgPrivacyPolicies(this.props.orgId, this.props.forceRefresh);
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        isDataLoaded(orgId) {
            return (
                !!this.state.orgPrivacyPolicies
            );
        }

        getOrgPrivacyPolicies(orgId, forceRefresh=false) {
            if (
                FeatureUtil.hasPrivacyPolicies(this.props.channelPermissions)
                && PermissionsUtil.canViewPrivacyPolicies(this.props.userPermissions)
            ) {
                if (
                    !forceRefresh
                    && this.isDataLoaded(orgId)
                ) { // if privacy policies are already populated do not refetch, unless forceRefresh is true
                    return;
                }

                const actionId = uuid.v4();
                this.setState({ getActionId: actionId }, () => {
                    OrganizationActions.getOrgPrivacyPolicies(actionId, { orgId: orgId });
                });
            }
            else {
                this.setState({ fatalError: new Error(Strings['en'].common.NOT_ALLOWED) });
            }
        }

        onOrganizationStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getActionId) {
                //TODO: do not set values in case of !!newState.action.error. potential referencing properties of undefined newState.assignmentData
                additionalState = { ...additionalState, getActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            const orgStoreState = OrganizationStore.getStateOrganization(this.props.orgId);
            this.setState({
                orgPrivacyPolicies: orgStoreState.orgPrivacyPolicies,
                ...additionalState
            });
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} />;
            }

            if (!this.isDataLoaded(this.props.orgId)) {
                return (
                    <div className="assignment-instructor">
                        <LoadingMainContent />
                    </div>
                );
            }

            return (
                <WrappedComponent
                    {...this.props}
                    orgPrivacyPolicies={this.state.orgPrivacyPolicies}
                    canEditPrivacyPolicies={PermissionsUtil.canEditPrivacyPolicies(this.props.userPermissions)}
                />
            );
        }
    }

    WithOrganizationPrivacyPolicies.propTypes = propTypes;
    WithOrganizationPrivacyPolicies.defaultProps = defaultProps;
    WithOrganizationPrivacyPolicies.displayName = `WithOrganizationPrivacyPolicies(${ReactUtil.getComponentDisplayName(WrappedComponent)})`;
    return WithOrganizationPrivacyPolicies;
}